<template>
  <div :class="['chat', { 'is-mobile': isMobile }]">
    <div class="layout">
      <div class="header">
        <span class="chat-title">{{ title }}</span>

        <div ref="closeBtnRef" class="close-button" @click="handleClose">
          <GamIcon :name="GamIconName.X_CLOSE" :size="GamIconSize.X_LARGE" />
        </div>
      </div>

      <ScrollOverflow ref="messageScrollRef" class="message-scroll" is-vertical>
        <div class="messages">
          <div v-if="areMessagesLoading" class="status start">Loading</div>

          <div v-else-if="chat.isActive && !isEnabled && chat.type === ChatType.GAMBIT" class="status start">
            Chat disabled until at least two players join.
          </div>

          <div v-else-if="chat.isActive && !isEnabled && chat.type === ChatType.SPARK" class="status start">
            Chat opens 1 hour before scheduled meeting time.
          </div>

          <div v-else class="status start">Chat started on {{ getShortDate(chat.createdAt) }}</div>

          <template v-for="(message, m) in messages" :key="`msg-${m}`">
            <Message
              v-if="message.user"
              v-bind="getMessageProps(message, messages, m)"
              :can-edit="isOrganizer || message.user.username === userDetails?.username"
            />
            <SystemMessage v-else v-bind="getMessageProps(message, messages, m)" />
          </template>

          <div v-if="!chat.isActive" class="status end">Chat closed on {{ getShortDate(chat.updatedAt) }}</div>
        </div>
      </ScrollOverflow>

      <div v-if="chat.isActive" class="input">
        <InputBox :is-disabled="!isEnabled" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ChatDto, MessageDto } from '@/core/data/chat/chat.interface';
import { ChatType } from '@/core/data/chat/chat.type';
import { getShortDate } from '@/core/helpers/date.helper';
import { isMobile } from '@/core/helpers/ui.helper';
import { useAuthStore } from '@/stores/auth.store';
import { useChatStore } from '@/stores/chat.store';
import GamIcon from '@/views/components/GamIcon.vue';
import ScrollOverflow from '@/views/components/ScrollOverflow.vue';
import { GamIconName, GamIconSize } from '@/views/composables/constants/components/gamIcon.constants';
import { GamComponentsEmits } from '@/views/composables/constants/main/emit.constants';
import Message, { type MessageProps } from '@/views/main/chat/Message.vue';
import { storeToRefs } from 'pinia';
import { nextTick } from 'process';
import { onBeforeMount, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import InputBox from './InputBox.vue';
import SystemMessage from './SystemMessage.vue';

export interface ChatProps {
  chat: ChatDto;
  title: string;
  isEnabled: boolean;
  isOrganizer?: boolean;
}

defineProps<ChatProps>();
const emit = defineEmits([GamComponentsEmits.CLOSE]);

const chatStore = useChatStore();
const { messages, areMessagesLoading } = storeToRefs(chatStore);
const authStore = useAuthStore();
const { userDetails } = storeToRefs(authStore);

const router = useRouter();
const route = useRoute();

const closeBtnRef = ref<HTMLDivElement>();
const messageScrollRef = ref<typeof ScrollOverflow>();

onBeforeMount(() => {
  window.visualViewport?.addEventListener('resize', setViewportHeight);
  window.addEventListener('resize', setViewportHeight);
  setViewportHeight();
});

onMounted(() => {
  if (closeBtnRef.value) {
    // closeBtnRef.value.addEventListener('touchstart', handleClose);
    closeBtnRef.value.addEventListener('touchmove', handleDrag, { passive: false });
  }

  nextTick(() => {
    if (!messageScrollRef.value) {
      return;
    }

    const linkedMessage = document.getElementById(String(route.query.message));

    if (linkedMessage) {
      linkedMessage && messageScrollRef.value.scrollTo({ top: linkedMessage.offsetTop - 64, behavior: 'instant' });
      setTimeout(() => router.replace({ ...route, query: { ...route.query, message: undefined } }), 5000);
    } else {
      messageScrollRef.value.scrollTo({ bottom: true, behavior: 'instant' });
    }
  });
});

watch(
  () => messages.value.length,
  () => {
    nextTick(() => {
      if (!messageScrollRef.value) {
        return;
      }

      messageScrollRef.value.scrollTo({ bottom: true, behavior: 'smooth' });
    });
  },
);

onBeforeUnmount(() => {
  window.visualViewport?.removeEventListener('resize', setViewportHeight);
  window.removeEventListener('resize', setViewportHeight);
  unsetViewportHeight();
});

const handleDrag = (e: Event) => {
  e.preventDefault();
};

const handleClose = () => {
  emit(GamComponentsEmits.CLOSE);
};

const setViewportHeight = () => {
  const vh = window.visualViewport ? window.visualViewport.height * 0.01 : window.innerHeight * 0.01;
  document.documentElement.style.setProperty('--vh', `${vh}px`);
  nextTick(() => {
    messageScrollRef.value?.scrollTo({ bottom: true, behavior: 'instant' });
  });
};

const unsetViewportHeight = () => {
  document.documentElement.style.removeProperty('--vh');
};

const getMessageProps = (message: MessageDto, messages: MessageDto[], m: number): MessageProps => {
  const prevUsername = m > 0 ? messages[m - 1].user?.username : undefined;
  const isSameAsPrev = prevUsername === message.user?.username;
  const isEven = m % 2 === 0;

  return {
    message,
    prevUsername,
    isSameAsPrev,
    isEven,
  };
};
</script>

<style lang="scss" scoped>
.chat {
  pointer-events: none;

  color: var(--color-white);
  background: var(--color-dark-900);
  animation: appear var(--transition);
}

.chat.is-mobile {
  background-color: var(--color-dark-700);
  background: #171716;
}

.header {
  box-sizing: content-box;
  position: absolute;
  top: 0;
  left: 0.375rem;
  right: 0.375rem;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 1.25rem calc(4rem - 0.375rem) 2rem;
  background: linear-gradient(180deg, var(--color-dark-800) 50%, transparent);
  line-height: 1.5rem;
  height: 1.5rem;
  z-index: 2;
}

.chat-title {
  font-weight: 600;
  letter-spacing: 0.02em;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  text-overflow: ellipsis;
  word-break: break-word;
  hyphens: auto;
  overflow: hidden;
  background: var(--color-linear-gradient);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.close-button {
  pointer-events: auto;
  position: absolute;
  padding: var(--spacing-l);
  top: var(--spacing-xs);
  right: var(--spacing-xs);
  z-index: 2;
}

.layout {
  pointer-events: none;

  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  height: 100%;
}

.status {
  font-size: 14px;
  color: var(--color-white-30);
  flex-shrink: 0;
  padding: 1.5rem 3.125rem 1rem;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  z-index: 1;
  text-wrap: balance;
  line-height: 1.4;
}

.status.start {
  padding: 0.5rem 3.125rem 1rem;
}

.status.end {
  padding: 1.5rem 3.125rem 1rem;
}

.message-scroll {
  pointer-events: auto;

  flex-grow: 1;
}

.chat .message-scroll {
  padding-top: 3rem;
}

.messages {
  padding: 1rem 1rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 1rem;
  gap: 0.5rem;
}

.is-mobile .messages {
  padding-bottom: calc(6.5rem + var(--sai-bottom));
}

.input {
  flex-shrink: 0;
  padding: 0 1rem 1rem;
}

.is-mobile .input {
  padding: 0 calc(3rem - 0.5rem) var(--sai-bottom);
  position: absolute;
  z-index: 2;
  bottom: 0;
  left: 0.5rem;
  right: 0.5rem;
  background: linear-gradient(transparent 0%, var(--color-dark-700) calc(100% - 1.5rem));
}
</style>
